import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { Phone, Mail, Clock, HelpCircle, CheckCircle2, Loader2, Users } from 'lucide-react';

type CommitteeContact = {
  departments: string[];
  committeeHead: {
    name: string;
    email: string;
    mobile: string | null;
  } | null;
};

const DEPT_PREVIEW_LIMIT = 4;

function DepartmentLabel({ departments }: { departments: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const needsReadMore = departments.length > DEPT_PREVIEW_LIMIT;
  const visible = expanded || !needsReadMore
    ? departments
    : departments.slice(0, DEPT_PREVIEW_LIMIT);
  const hiddenCount = departments.length - DEPT_PREVIEW_LIMIT;

  return (
    <span className="inline">
      {visible.join(', ')}
      {needsReadMore && !expanded && (
        <>
          {', '}
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-blue-600 hover:underline font-semibold text-sm"
          >
            +{hiddenCount} more
          </button>
        </>
      )}
      {needsReadMore && expanded && (
        <>
          {' '}
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-blue-600 hover:underline font-semibold text-sm"
          >
            show less
          </button>
        </>
      )}
    </span>
  );
}

export function Help() {
  const isLoggedIn = Boolean(localStorage.getItem('accessToken'));

  const { data: committeeContacts, isLoading: isLoadingContacts } = useQuery({
    queryKey: ['committee-contacts'],
    queryFn: async () => {
      const res = await apiClient.get('/ideas/committee-contacts');
      return (res.data.data || []) as CommitteeContact[];
    },
    enabled: isLoggedIn,
  });

  return (
    <Layout title="Help & Support">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-6 md:p-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">Need assistance with submitting your idea or have questions about the process?</h2>
            <p className="text-slate-600 mb-8">We're here to help.</p>

            <h3 className="text-lg font-semibold text-slate-800 mb-4 border-b border-slate-100 pb-2">Get in Touch</h3>
            <p className="text-slate-600 mb-6">
              If you face any issues while submitting your idea, tracking its status, or navigating the portal, feel free to reach out to our support team.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start space-x-4 bg-slate-50 p-4 rounded-lg border border-slate-100">
                <Phone className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <p className="font-semibold text-slate-800">Phone</p>
                  <a href="tel:+919043995078" className="text-blue-600 hover:underline">+91 90439 95078</a>
                </div>
              </div>

              <div className="flex items-start space-x-4 bg-slate-50 p-4 rounded-lg border border-slate-100">
                <Mail className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <p className="font-semibold text-slate-800">Email</p>
                  <a href="mailto:sasitharan.nr@casagrand.co.in" className="text-blue-600 hover:underline">sasitharan.nr@casagrand.co.in</a>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-4 mb-8">
              <Clock className="w-6 h-6 text-slate-400 mt-1" />
              <div>
                <p className="font-semibold text-slate-800">Support Hours</p>
                <p className="text-slate-600">Monday to Saturday, 9:30 AM – 6:30 PM</p>
              </div>
            </div>

            {isLoggedIn && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-slate-800 mb-2 border-b border-slate-100 pb-2">
                  Your Idea Department Contacts
                </h3>
                <p className="text-slate-600 mb-6">
                  Committee heads for departments related to ideas you have submitted.
                </p>

                {isLoadingContacts ? (
                  <div className="flex items-center py-4 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    <span className="text-sm">Loading contacts…</span>
                  </div>
                ) : !committeeContacts || committeeContacts.length === 0 ? (
                  <div className="flex items-start space-x-3 text-slate-500">
                    <Users className="w-5 h-5 text-slate-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">
                      No submitted ideas yet. Department committee contacts will appear here after you submit an idea.
                    </span>
                  </div>
                ) : (
                  <ul className="space-y-4">
                    {committeeContacts.map((item) => (
                      <li key={item.departments.join('|')} className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="text-slate-800 font-semibold text-sm">
                            <DepartmentLabel departments={item.departments} />
                            <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded">
                              Committee Head
                            </span>
                          </p>

                          {item.committeeHead ? (
                            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                              <span className="font-medium text-slate-700">
                                {item.committeeHead.name}
                              </span>
                              <span className="hidden sm:inline text-slate-200">|</span>
                              {item.committeeHead.mobile ? (
                                <a
                                  href={`tel:${item.committeeHead.mobile}`}
                                  className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"
                                >
                                  <Phone className="w-3.5 h-3.5" />
                                  {item.committeeHead.mobile}
                                </a>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 text-slate-400">
                                  <Phone className="w-3.5 h-3.5" />
                                  N/A
                                </span>
                              )}
                              <span className="hidden sm:inline text-slate-200">|</span>
                              <a
                                href={`mailto:${item.committeeHead.email}`}
                                className="inline-flex items-center gap-1.5 text-blue-600 hover:underline truncate max-w-full"
                              >
                                <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="truncate">{item.committeeHead.email}</span>
                              </a>
                            </div>
                          ) : (
                            <p className="mt-1 text-sm text-slate-400">
                              No committee head mapped for this department.
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <h3 className="text-lg font-semibold text-slate-800 mb-4 border-b border-slate-100 pb-2">Common Areas We Can Help With:</h3>
            <ul className="space-y-3 mb-8">
              {[
                'Trouble logging in or accessing your account',
                'Issues while submitting or editing an idea',
                'Clarification on submission guidelines or eligibility',
                'Status updates on submitted ideas',
                'Technical issues on the portal (upload errors, page not loading, etc.)'
              ].map((item, index) => (
                <li key={index} className="flex items-start space-x-3 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="bg-blue-50 text-blue-800 p-4 rounded-lg flex items-center space-x-3">
              <HelpCircle className="w-5 h-5 flex-shrink-0" />
              <p className="font-medium">We aim to respond to all queries within 24-48 business hours.</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
